/**
 * Data layer for the report/dashboard pages.
 *
 * Every figure on the report pages is live. Four services back the module
 * (all `POST`, all under `management/api/Reports`):
 *
 *   - GetOverviewDashboard  — total usage, top consumers, meters with alarms
 *   - GetMetersReport       — meter counts + daily consumption series
 *   - GetSubscribersReport  — subscriber counts, per jurisdiction / month, recents
 *   - GetApartmentsReport   — apartment & unit figures, top consumers
 *
 * The response contract is documented in ./BACKEND_SERVICES.md. Mapping below
 * is intentionally null-safe: a missing or renamed field falls back to 0 / ""
 * / [] instead of throwing inside a chart or grid.
 */

import { netCall } from "utils/netCall";
import { TPayloadData, TResponseCode } from "utils/netCall/types";
import { ManagementApiUrl } from "environment/routeSettings";
import {
  TOverviewReport,
  WaterMeterAlarmsType,
  WaterSubscriberType,
} from "types/dto";
import i18n from "../../../react-i18next";
import { format } from "date-fns-jalali";
import {
  TApartmentsReport,
  TApartmentsReportResult,
  TMetersReport,
  TMetersReportResult,
  TNameValue,
  TReportFilters,
  TReportServiceBody,
  TSubscribersReport,
  TSubscribersReportResult,
} from "./types";

/** Auto refresh interval for the live dashboards. */
export const REPORTS_REFRESH_INTERVAL_MS = 60_000;

/** Default range length (days) when the page filters carry no dates. */
export const REPORTS_PERIOD_DAYS = 7;

/** How many months of history the trend charts ask for. */
export const REPORTS_MONTHS_BACK = 6;

/** How many rows the "recent" / "top" lists ask for. */
export const REPORTS_LIST_SIZE = 10;

/**
 * Effective "no limit" for GetOverviewDashboard's `topN`. The service requires
 * the field, so an unreachable ceiling is how the meters report asks for every
 * meter in the region instead of a pre-sliced top-N.
 */
export const REPORTS_ALL_ROWS = 100_000;

/**
 * The meters report pulls every meter's usage, so it refreshes far less often
 * than the pages that fetch a handful of aggregate rows — a payload that size
 * every 60s is wasteful per open tab.
 */
export const METERS_REPORT_REFRESH_INTERVAL_MS = 300_000;

/** Top-N slice sizes offered on the meters report (applied client-side). */
export const TOP_N_OPTIONS = [10, 20, 50];

/* --------------------------- request plumbing --------------------------- */

export type TOverviewBody = TReportFilters & {
  subscriberType?: WaterSubscriberType;
  startD?: string;
  endD?: string;
  topN?: number;
};

const isoDate = (d: Date) => d.toISOString().split("T")[0];

const daysAgo = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d;
};

/** Resolves the requested date range, falling back to the last REPORTS_PERIOD_DAYS days. */
const resolveRange = (filters: TReportFilters) => ({
  startD: filters.startD || isoDate(daysAgo(REPORTS_PERIOD_DAYS)),
  endD: filters.endD || isoDate(new Date()),
});

/** The region + range body shared by the three report services. */
const toServiceBody = (filters: TReportFilters): TReportServiceBody => ({
  provinceId: filters.provinceId,
  cityId: filters.cityId,
  jurisdictionId: filters.jurisdictionId,
  ...resolveRange(filters),
});

/** POSTs to a Reports endpoint and unwraps the standard response envelope. */
const postReport = async <TResult, TBody extends TPayloadData>(
  action: string,
  body: TBody
): Promise<TResult> => {
  const res = await netCall<TResult>({
    method: "POST",
    endPoint: `${ManagementApiUrl()}/api/Reports/${action}`,
    body,
    preventErrorToast: true, // pages poll silently; errors are handled by the caller
  });
  if (res.responseCode !== TResponseCode.OK) {
    throw new Error(`Failed to get ${action} data`);
  }
  return res.results;
};

export const getOverviewDashboard = (
  body: TOverviewBody
): Promise<TOverviewReport> =>
  postReport<TOverviewReport, TOverviewBody>("GetOverviewDashboard", body);

/* ---------------------------- mapping helpers --------------------------- */

/** Finite number or `fallback` — guards against null/undefined/NaN from the API. */
const num = (value: unknown, fallback = 0): number =>
  typeof value === "number" && Number.isFinite(value) ? value : fallback;

const str = (value: unknown, fallback = ""): string =>
  typeof value === "string" && value.length > 0 ? value : fallback;

const list = <T>(value: T[] | null | undefined): T[] =>
  Array.isArray(value) ? value : [];

/** Digits in the active UI locale (Persian digits under `fa`). */
const localeDigits = (value: number): string =>
  value.toLocaleString(i18n.language?.startsWith("fa") ? "fa-IR" : "en-US", {
    useGrouping: false,
  });

/**
 * Jalali month label for a Gregorian year/month pair. The services return
 * Gregorian `year`/`month` (see BACKEND_SERVICES.md) and the label is rendered
 * here so the axis matches the rest of the UI.
 */
const jalaliMonthLabel = (year?: number, month?: number): string => {
  if (!year || !month) return "";
  return format(new Date(year, month - 1, 15), "MMMM");
};

/** Maps `[{ year, month, count }]` to chart-ready name/value pairs. */
const toMonthlySeries = (
  items: { year?: number; month?: number; count?: number }[] | null | undefined
): TNameValue[] =>
  list(items).map((item) => ({
    name: jalaliMonthLabel(item.year, item.month),
    value: num(item.count),
  }));

/** i18n key for a WaterSubscriberType value (falls back to "-"). */
const subscriberTypeKey = (type?: WaterSubscriberType): string =>
  type && WaterSubscriberType[type]
    ? `enum-${WaterSubscriberType[type]}`
    : "enum-None";

/** Decomposes `latestAlarmOnWm` flags of all error meters into counts per alarm type. */
export const groupAlarmsByType = (
  errors: TOverviewReport["waterMeterWithErrors"]
): TNameValue[] => {
  const counts = new Map<string, number>();
  list(errors).forEach((item) => {
    alarmFlagLabels(item.latestAlarmOnWm).forEach((label) => {
      counts.set(label, (counts.get(label) || 0) + 1);
    });
  });
  return Array.from(counts.entries())
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);
};

/** Translated labels for a WaterMeterAlarmsType flags value. */
export const alarmFlagLabels = (value: WaterMeterAlarmsType): string[] => {
  if (!value) return [i18n.t("enum-None")];
  return Object.keys(WaterMeterAlarmsType)
    .filter((key) => {
      const enumValue = (WaterMeterAlarmsType as any)[key];
      return typeof enumValue === "number" && (value & enumValue) !== 0;
    })
    .map((key) => i18n.t("enum-" + key));
};

/* ------------------------------- Meters -------------------------------- */

export const getMetersReport = async (
  filters: TReportFilters
): Promise<TMetersReport> => {
  const today = new Date();
  const range = resolveRange(filters);

  const [metersReport, todayReport, periodReport] = await Promise.all([
    // meter counts + daily consumption series for the selected range
    postReport<TMetersReportResult, TReportServiceBody>(
      "GetMetersReport",
      toServiceBody(filters)
    ),
    // consumption of today only
    getOverviewDashboard({
      ...filters,
      startD: isoDate(today),
      endD: isoDate(today),
      topN: 1,
    }),
    // selected range: EVERY meter's usage + meters with errors. The page slices
    // its own top-N from this, so changing the slice size costs no request.
    getOverviewDashboard({
      ...filters,
      ...range,
      topN: REPORTS_ALL_ROWS,
    }),
  ]);

  const activeMeters = num(metersReport?.activeMeters);
  const inactiveMeters = num(metersReport?.inactiveMeters);
  const noSignalMeters = num(metersReport?.noSignalMeters);

  return {
    // GetMetersReport
    totalMeters: num(metersReport?.totalMeters),
    activeMeters,
    inactiveMeters,
    noSignalMeters,
    statusBreakdown: [
      { name: i18n.t("active"), value: activeMeters },
      { name: i18n.t("inactive"), value: inactiveMeters },
      { name: i18n.t("noSignal"), value: noSignalMeters },
    ],
    dailyConsumption: list(metersReport?.dailyConsumption).map((point) => {
      const date = point.date ? new Date(point.date) : new Date(NaN);
      const time = date.getTime();
      return {
        date: Number.isNaN(time) ? 0 : time,
        label: Number.isNaN(time) ? str(point.date) : format(date, "MM/dd"),
        value: num(point.usage),
      };
    }),

    // GetOverviewDashboard
    consumptionToday: num(todayReport?.totalWaterUsage),
    consumptionInRange: num(periodReport?.totalWaterUsage),
    metersWithAlarm: list(periodReport?.waterMeterWithErrors).length,
    alarmsByType: groupAlarmsByType(periodReport?.waterMeterWithErrors),
    // Sorted desc so the page can slice a top-N off the front. The service
    // orders by usage already; sorting here keeps that a local guarantee.
    meterUsage: list(periodReport?.topNWaterUsage)
      .map((item) => ({
        id: item.waterMeterId,
        identifierId: item.identifierId || item.waterMeterId,
        consumption: num(item.usage),
        latitude: num(item.latitude),
        longitude: num(item.longitude),
      }))
      .sort((a, b) => b.consumption - a.consumption),
    metersWithErrors: list(periodReport?.waterMeterWithErrors).map((item) => ({
      id: item.waterMeterId,
      identifierId: item.identifierId || item.waterMeterId,
      alarmLabel: alarmFlagLabels(item.latestAlarmOnWm).join("، "),
      latitude: num(item.latitude),
      longitude: num(item.longitude),
    })),
  };
};

/* ----------------------------- Subscribers ----------------------------- */

export type TSubscribersReportBody = TReportServiceBody & {
  monthsBack?: number;
  recentCount?: number;
};

export const getSubscribersReport = async (
  filters: TReportFilters
): Promise<TSubscribersReport> => {
  const period = { ...resolveRange(filters), topN: 1 };
  const types = [
    WaterSubscriberType.Private,
    WaterSubscriberType.Commercial,
    WaterSubscriberType.Governmental,
  ];

  const [subscribersReport, usageReports] = await Promise.all([
    postReport<TSubscribersReportResult, TSubscribersReportBody>(
      "GetSubscribersReport",
      {
        ...toServiceBody(filters),
        monthsBack: REPORTS_MONTHS_BACK,
        recentCount: REPORTS_LIST_SIZE,
      }
    ),
    // total usage per subscriber type over the selected range
    Promise.all(
      types.map((subscriberType) =>
        getOverviewDashboard({ ...filters, ...period, subscriberType })
      )
    ),
  ]);

  const usageByType: TNameValue[] = types.map((type, index) => ({
    name: i18n.t(subscriberTypeKey(type)),
    value: num(usageReports[index]?.totalWaterUsage),
  }));

  return {
    // GetSubscribersReport
    totalSubscribers: num(subscribersReport?.totalSubscribers),
    activeSubscribers: num(subscribersReport?.activeSubscribers),
    inactiveSubscribers: num(subscribersReport?.inactiveSubscribers),
    newSubscribersThisMonth: num(subscribersReport?.newSubscribersThisMonth),
    byJurisdiction: list(subscribersReport?.byJurisdiction).map((item) => ({
      name: str(item.jurisdictionName, "-"),
      value: num(item.count),
    })),
    monthlyNewSubscribers: toMonthlySeries(
      subscribersReport?.monthlyNewSubscribers
    ),
    recentSubscribers: list(subscribersReport?.recentSubscribers).map(
      (item, index) => ({
        id: str(item.waterSubscriberId, `recent-${index}`),
        subscriptionNumber: str(item.subscriptionNumber, "-"),
        fullName: str(item.fullName, "-"),
        subscriberTypeKey: subscriberTypeKey(item.subscriberType),
        jurisdictionName: str(item.jurisdictionName, "-"),
        registeredAt: str(item.registeredAt),
      })
    ),

    // GetOverviewDashboard
    usageByType,
  };
};

/* ------------------------------ Apartments ----------------------------- */

export type TApartmentsReportBody = TReportServiceBody & {
  monthsBack?: number;
  topN?: number;
};

/** Bucket label for a unitsDistribution entry ("۶-۱۰", "۲۱+"). */
const unitsBucketLabel = (minUnits?: number, maxUnits?: number | null) => {
  const min = num(minUnits);
  if (maxUnits === null || maxUnits === undefined) {
    return `${localeDigits(min)}+`;
  }
  return `${localeDigits(min)}-${localeDigits(num(maxUnits))}`;
};

export const getApartmentsReport = async (
  filters: TReportFilters
): Promise<TApartmentsReport> => {
  const apartmentsReport = await postReport<
    TApartmentsReportResult,
    TApartmentsReportBody
  >("GetApartmentsReport", {
    ...toServiceBody(filters),
    monthsBack: REPORTS_MONTHS_BACK,
    topN: filters.topN || REPORTS_LIST_SIZE,
  });

  return {
    totalApartments: num(apartmentsReport?.totalApartments),
    totalUnits: num(apartmentsReport?.totalUnits),
    apartmentsWithActiveMeters: num(
      apartmentsReport?.apartmentsWithActiveMeters
    ),
    averageUnitsPerApartment: Number(
      num(apartmentsReport?.averageUnitsPerApartment).toFixed(1)
    ),
    byCity: list(apartmentsReport?.byCity).map((item) => ({
      name: str(item.cityName, "-"),
      value: num(item.count),
    })),
    unitsDistribution: list(apartmentsReport?.unitsDistribution).map(
      (item) => ({
        name: unitsBucketLabel(item.minUnits, item.maxUnits),
        value: num(item.count),
      })
    ),
    monthlyRegistered: toMonthlySeries(apartmentsReport?.monthlyRegistered),
    topApartmentsByConsumption: list(
      apartmentsReport?.topApartmentsByConsumption
    ).map((item, index) => ({
      id: str(item.apartmentId, `apartment-${index}`),
      name: str(item.name, "-"),
      cityName: str(item.cityName, "-"),
      unitCount: num(item.unitCount),
      meterCount: num(item.meterCount),
      consumption: num(item.usage),
    })),
  };
};
